import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Brain, Users, Key, Settings, BarChart2, Compass, Network, FolderTree, Target, Calendar, Edit2, Activity, FileText } from '../../icons';
import { SidebarLayout } from '../SidebarLayout';
import { RoleSwitcherTR } from '../../tr/RoleSwitcherTR';
import { useAuth } from '../../../contexts/AuthContext';
import { isUserCoach } from '../../../lib/auth';
import { supabase } from '../../../lib/supabase';
import { LoadingSpinner } from '../../LoadingSpinner';
import type { SidebarItem } from '../Sidebar';

export function CoachLayoutTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [authorized, setAuthorized] = useState(false);
  const [checking, setChecking] = useState(true);
  const [hasAIAccess, setHasAIAccess] = useState(false);
  const [hasTeamsAccess, setHasTeamsAccess] = useState(false);
  const [hasCRMAccess, setHasCRMAccess] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/tr/login');
      return;
    }

    const init = async () => {
      const isCoach = await isUserCoach(user.id);
      if (!isCoach) {
        navigate('/tr');
        return;
      }

      const { data: coachData } = await supabase
        .from('coaches')
        .select('tier, teams_access_enabled, crm_enabled')
        .eq('user_id', user.id)
        .maybeSingle();

      setHasAIAccess(coachData?.tier === 'advanced' || coachData?.tier === 'partner');
      setHasTeamsAccess(coachData?.teams_access_enabled === true);
      setHasCRMAccess(coachData?.crm_enabled === true);
      setAuthorized(true);
      setChecking(false);
    };

    init();
  }, [user, authLoading, navigate]);

  if (authLoading || checking || !authorized) {
    return <LoadingSpinner message="Yukleniyor..." />;
  }

  const items: SidebarItem[] = [
    // Profile
    { group: 'Profile', label: 'Kontrol Paneli', path: '/tr/coach', icon: Brain },
    { group: 'Profile', label: 'Katilimcilar', path: '/tr/coach/respondents', icon: Users },
    { group: 'Profile', label: 'Degerlendirme Kodlari', path: '/tr/coach/codes', icon: Key },
    { group: 'Profile', label: 'Projeler', path: '/tr/coach/projects', icon: FolderTree, gated: !hasTeamsAccess, gateMessage: 'Etkinlestirmek icin partnerinize basvurun' },
    { group: 'Profile', label: 'Takimlar', path: '/tr/coach/teams', icon: Network, gated: !hasTeamsAccess, gateMessage: 'Etkinlestirmek icin partnerinize basvurun' },

    // Pattern
    { group: 'Pattern', label: 'YZ Analizi', path: '/tr/coach/manual-analysis', icon: Brain, gated: !hasAIAccess, gateMessage: 'Ileri seviye gerektirir' },

    // Progress
    { group: 'Progress', label: 'Musteri Ilerlemesi', path: '/tr/coach/progress', icon: Activity },
    { group: 'Progress', label: 'Transformation Reports', path: '/tr/coach/transformation-reports', icon: FileText },

    // Pathways
    { group: 'Pathways', label: 'Transformation Pathways', path: '/tr/coach/pathways', icon: Compass, gated: !hasAIAccess, gateMessage: 'Ileri seviye gerektirir' },
    { group: 'Pathways', label: 'Habit Installation OS\u2122', path: '/tr/coach/habit-os', icon: Target },

    // Presence
    { group: 'Presence', label: 'Kocluk Seanslari', path: '/tr/coach/sessions', icon: Calendar, gated: !hasCRMAccess, gateMessage: 'Etkinlestirmek icin partnerinize basvurun' },

    // Proof
    { group: 'Proof', label: 'Takim Raporlari', path: '/tr/coach/reports', icon: BarChart2, gated: !hasTeamsAccess, gateMessage: 'Etkinlestirmek icin partnerinize basvurun' },
    { group: 'Proof', label: 'Bireysel Raporlar', path: '/tr/coach/coming-soon/individual-reports', icon: BarChart2, gated: true, gateMessage: 'Yak\u0131nda' },

    // Promote
    { group: 'Promote', label: 'Tanitim Sayfasi', path: '/tr/coach/lead-page', icon: Edit2, gated: !hasCRMAccess, gateMessage: 'Etkinlestirmek icin partnerinize basvurun' },
    { group: 'Promote', label: 'Satis Hatti', path: '/tr/coach/crm', icon: Target, gated: !hasCRMAccess, gateMessage: 'Etkinlestirmek icin partnerinize basvurun' },

    // Settings
    { group: 'Settings', label: 'YZ Koc Ayarlari', path: '/tr/coach/ai-settings', icon: Settings, gated: !hasAIAccess, gateMessage: 'Ileri seviye gerektirir' },
    { group: 'Settings', label: 'YZ Koc Kullanimi', path: '/tr/coach/ai-usage', icon: BarChart2, gated: !hasAIAccess, gateMessage: 'Ileri seviye gerektirir' },
  ];

  return (
    <SidebarLayout
      items={items}
      title="Koc"
      subtitle="Katilimcilari ve icgoruleri yonetin"
      headerSlot={<RoleSwitcherTR currentRole="coach" />}
    />
  );
}
